import { MessageCircleIcon } from "lucide-react"
import type * as React from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import { buttonVariants } from "@/components/ui/button"
import { useCan } from "@/features/auth/auth-context"
import { useDirectUnread } from "@/features/messages/use-direct-messages"
import { useMessagesUnread } from "@/features/messages/use-messages"
import { cn } from "@/lib/utils"
import { routes } from "@/routes/paths"

/**
 * The way into the conversation, in the app chrome (§11.2, §2).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * IT SITS BESIDE THE BELL, IN THE SAME CORNER, BUILT THE SAME WAY — AND IT IS A
 * SECOND CONTROL RATHER THAN A SECOND MEANING FOR THE FIRST.
 *
 * "One place to look" is the requirement, and this satisfies it the way a phone
 * does: the two things that can be waiting for you live in one corner of the
 * header, identical in size, shape and behaviour, so there is one glance rather
 * than a hunt. What it deliberately does NOT do is merge the two counts into the
 * bell.
 *
 * WHY MERGING THEM WOULD BE WORSE, CONCRETELY:
 *
 *  1. NEITHER ACTION COULD CLEAR IT. A notification is cleared per row, by the
 *     person it is addressed to; the conversation is cleared by a watermark when
 *     you read it. A single "4" made of two alerts and two messages goes to "2"
 *     when you read the conversation, and the reader cannot tell whether the
 *     remaining two are compliance or chatter.
 *  2. ONE ICON CANNOT OPEN TWO SCREENS. Tapping would have to pick one, and the
 *     wrong pick is the common case exactly when it matters.
 *  3. IT WOULD DILUTE THE ONE ALERT THAT IS A LEGAL DEADLINE. "This bill needs
 *     an e-way bill" is a detention if it is missed. A badge that also lights up
 *     because somebody said "ok" is a badge that gets ignored, and then so does
 *     the one that mattered.
 *
 * So: same corner, same chrome, different icon, different count.
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * THREE STATES, MIRRORING `NotificationBell` EXACTLY:
 *
 *   NO CAPABILITY     nothing, and NO REQUEST EITHER. Each half is checked
 *                     before its query runs, so a person who holds neither
 *                     never polls a route that would 403 him twice a minute —
 *                     and `requireCap` audits every denial (§2.1).
 *   MODULE OFF (404)  nothing. §18.2 — a disabled feature is invisible, not
 *                     empty. A control that opens "no messages yet" promises a
 *                     conversation that cannot happen while the flag is off.
 *   ON                the icon, with a badge WHEN AND ONLY WHEN something has
 *                     been said since you last looked.
 *
 * IT OPENS `/messages`, WHICH IS THE LIST — the people, and the group chat as a
 * row below them (2026-08-19). One control, one door, whichever kind of
 * conversation the count is made of; the reader picks the row rather than the
 * badge picking for them.
 */
export function MessagesButton(): React.JSX.Element | null {
  const { t } = useTranslation()
  const canView = useCan("message.view")
  /*
   * ═══════════════════════════════════════════════════════════════════════════
   * THE BADGE COUNTS BOTH HALVES OF THE MESSENGER (2026-08-18) — THE ROOM AND
   * THE PRIVATE CONVERSATIONS.
   *
   * WHY THAT IS NOT THE MERGE THIS FILE ARGUES AGAINST ABOVE. The three
   * objections there are all about folding MESSAGES INTO THE BELL: two things
   * cleared by different actions, one icon that would have to pick a screen, and
   * a legal deadline diluted by chatter. None of them applies to the two halves
   * of ONE messenger. Both are cleared by reading a conversation, both are
   * reached through this one control, and neither is a compliance alert. What
   * would be genuinely worse is the alternative: a private message waiting with
   * NOTHING anywhere in the chrome to say so, because the only surface that
   * could have said it was already spoken for by the room.
   *
   * THE SERVER KEEPS THE TWO NUMBERS APART, AND IS RIGHT TO.
   * `GET /messages/unread-count` still counts the room alone — that contract
   * shipped, and quietly folding DMs into it would change what an existing
   * client's badge means without the client asking. Adding them is therefore the
   * CLIENT's decision, taken here, in the one place the sum is displayed.
   *
   * EACH HALF IS GATED ON ITS OWN CAPABILITY, and neither request is made
   * without it. A person without `message.direct` polls nothing private and
   * their badge is the room's alone — the private half is not merely hidden from
   * them, it is not requested, so `requireCap` never audits a denial on their
   * behalf twice a minute (§2.1).
   * ═══════════════════════════════════════════════════════════════════════════
   */
  const canDirect = useCan("message.direct")
  const room = useMessagesUnread(canView)
  /*
   * `canDirect` ALONE, AND IT USED TO BE `canView && canDirect` (2026-08-19).
   *
   * The extra term was correct for the shape the messenger had: the ONLY door to
   * the private conversations was a link on the room's screen, itself behind
   * `message.view`, so somebody holding `message.direct` without it had no
   * reachable private surface and polling for a number nobody could ever see was
   * pure cost. `/messages` is the LIST now and it opens for EITHER capability, so
   * that person has a perfectly reachable surface — and dropping their count
   * would leave a private message waiting with nothing in the chrome to say so.
   *
   * Nobody is in that position today (all three roles hold `message.view`),
   * which is exactly why the term had to be revisited rather than inherited.
   */
  const direct = useDirectUnread(canDirect)

  /*
   * NOTHING AT ALL WITHOUT EITHER CAPABILITY — and neither request was made, so
   * `requireCap` never audited a denial on their behalf twice a minute (§2.1).
   */
  if (!canView && !canDirect) return null

  /*
   * §18.2 — AND NOTHING WHILE THE MODULE IS OFF. `available` is false when the
   * flag's 404 came back, while a request is in flight, and when one failed. The
   * control appears once EITHER half has answered, which is what lets a reader
   * holding one capability see their own count; if neither has, there is nothing
   * truthful to draw.
   */
  if (!room.available && !direct.available) return null

  /*
   * THE SUM, AND NOTHING IS COUNTED TWICE. The two numbers come from different
   * tables and different watermarks: `GET /messages/unread-count` counts the
   * shared room alone, `GET /messages/direct` totals the private conversations
   * from the server's own pair-wise watermarks. No message is in both.
   *
   * THE SERVER KEEPS THEM APART AND IS RIGHT TO — that room contract shipped, and
   * quietly folding private messages into it would change what an existing
   * client's badge means without the client asking. Adding them is the CLIENT's
   * decision, taken here, in the one place the sum is displayed.
   *
   * EACH HALF IS ADDED ONLY ONCE IT HAS ACTUALLY ARRIVED: `unread` is zero while
   * unavailable, so a failure UNDERSTATES the badge rather than inventing a
   * number. Understating is the safe direction for the same reason it is for the
   * watermark — the count reappears on the next poll, whereas a badge cleared
   * wrongly is cleared for ever, because there is no per-message read state and
   * no "mark unread" anywhere in this system.
   */
  const unread = room.unread + direct.unread

  /*
   * THE CONTROL STAYS AT ZERO; THE BADGE DOES NOT — the bell's reasoning,
   * unchanged. Hiding the whole control when nothing is new would make the
   * conversation unreachable exactly when somebody wants to go back and check
   * what was agreed last week, and a chrome element that appears and vanishes is
   * one the eye stops trusting.
   */
  const hasUnread = unread > 0

  return (
    /*
     * A PLAIN `<Link>` WEARING THE BUTTON'S CLASSES, not `<Button render={<Link/>}>`.
     * The Button primitive stamps `role="button"` on whatever it renders, which
     * on an anchor is a lie: this NAVIGATES, and middle-click / open-in-new-tab
     * are link affordances the role denies. Identical reasoning, and identical
     * markup, to `NotificationBell`.
     */
    <Link
      to={routes.messages}
      className={cn(buttonVariants({ variant: "ghost", size: "icon" }), "relative")}
      /*
       * THE COUNT IS IN THE ACCESSIBLE NAME, not only in the badge — a screen
       * reader landing on a bare "Messages" would have no way to learn that two
       * are waiting. `count` also drives i18next's plural selection, so Marathi
       * gets its own two forms.
       */
      aria-label={hasUnread ? t("chat.buttonWithCount", { count: unread }) : t("chat.button")}
      data-testid="messages-button"
    >
      <MessageCircleIcon />
      {hasUnread ? (
        /*
         * `aria-hidden`: the number is already in the label above, and a screen
         * reader announcing "Messages, 2 new — 2" reads as a stutter. Capped at
         * 99+ for the same reason the bell's is: the badge sits inside a 32px
         * control.
         */
        <span
          aria-hidden="true"
          data-testid="messages-badge"
          className="bg-primary text-primary-foreground absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] leading-none font-medium tabular-nums"
        >
          {unread > 99 ? "99+" : unread}
        </span>
      ) : null}
    </Link>
  )
}
